<!--
 * @Author: Antigravity
 * @Date: 2026-03-24
 * @Description: 投诉件分析-投诉件分类 Cell
-->

<template>
  <div class="w-full flex flex-col">
    <div class="w-full flex items-end justify-between my-16">
      <div class="text-16 font-bold text-black-primary">投诉件分类</div>
    </div>

    <div class="card p-16 flex flex-col w-full">
      <div v-if="loading" class="state-text">
        加载中...
      </div>
      <div v-else-if="!list.length" class="state-text">暂无数据</div>
      <template v-else>
        <!-- 顶部山峰图 -->
        <div class="relative w-full">
          <div class="tips-right">点击图表切换分类详情</div>
          <div class="chart-scroll" ref="chartScroll">
            <div ref="chartEl" class="chart" :style="{ width: chartWidth + 'px' }"></div>
          </div>
          <!-- 滚动条指示器 -->
          <div class="scroll-indicator-bg" v-if="showScrollIndicator">
            <div class="scroll-indicator-bar" :style="{ left: scrollPercent + '%', width: indicatorWidth + '%' }"></div>
          </div>
        </div>

        <!-- 详情卡片 -->
        <div v-if="selectedData" class="detail-card mt-16">
          <div class="detail-header p-12">
            <span class="detail-name">{{ selectedData.label }}</span>
            <div class="detail-summary">
              <span class="total-num">{{ formatNumber(selectedData.total) }}</span>
              <span class="unit">件</span>
              <span class="ratio-label ml-12">占比</span>
              <span class="ratio-val">{{ selectedData.ratio }}</span>
            </div>
          </div>

          <div class="detail-body px-16 pt-16 pb-8">
            <div class="sub-title mb-12">具体问题分类</div>
            <div class="issue-list">
              <div v-for="(child, idx) in visibleChildren" :key="idx"
                class="issue-item py-12 flex justify-between items-center">
                <div class="issue-name">{{ child.label }}</div>
                <div class="issue-stats">
                  <span class="val">{{ formatNumber(child.total) }}件</span>
                  <span class="ratio ml-12">{{ formatRationStr(child.ratio) }}</span>
                </div>
              </div>
            </div>

            <!-- 展开/收起 -->
            <div v-if="selectedData.children && selectedData.children.length > 3"
              class="expand-btn flex items-center justify-center py-8" @click="isExpand = !isExpand">
              <span class="mr-4">{{ isExpand ? '收起' : '展开' }}</span>
              <van-icon :name="isExpand ? 'arrow-up' : 'arrow-down'" />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import echarts from "echarts";
import APIS from "@/service/modules/complaintApis/modules/visualization.js";
import visualFilterMixin from "../../mixins/visualFilterMixin";

export default {
  name: "ComplaintClassifyCell",
  mixins: [visualFilterMixin],
  data() {
    return {
      chart: null,
      loading: false,
      list: [],
      selectedIndex: 0,
      isExpand: false,
      chartWidth: 320,
      scrollX: 0,
      containerWidth: 320,
      _onResize: null
    };
  },
  computed: {
    selectedData() {
      return this.list[this.selectedIndex] || null;
    },
    visibleChildren() {
      if (!this.selectedData || !this.selectedData.children) return [];
      if (this.isExpand) return this.selectedData.children;
      return this.selectedData.children.slice(0, 3);
    },
    showScrollIndicator() {
      return this.chartWidth > this.containerWidth;
    },
    indicatorWidth() {
      if (!this.containerWidth) return 0;
      return (this.containerWidth / this.chartWidth) * 100;
    },
    scrollPercent() {
      if (this.chartWidth <= this.containerWidth) return 0;
      const maxScroll = this.chartWidth - this.containerWidth;
      return (this.scrollX / maxScroll) * (100 - this.indicatorWidth);
    }
  },
  watch: {
    list: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.selectedIndex = 0;
          this.renderChart();
        });
      }
    }
  },
  mounted() {
    this.ensureChart();
    if (this.$refs.chartScroll) {
      this.$refs.chartScroll.addEventListener('scroll', this.handleScroll);
    }
  },
  beforeDestroy() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this.chart) this.chart.dispose();
    if (this.$refs.chartScroll) {
      this.$refs.chartScroll.removeEventListener('scroll', this.handleScroll);
    }
  },
  methods: {
    handleScroll(e) {
      this.scrollX = e.target.scrollLeft;
    },
    ensureChart() {
      const el = this.$refs.chartEl;
      if (!el) return;
      // v-if 切换后 DOM 会重建，旧 chart 实例的 DOM 不再匹配
      if (this.chart) {
        if (typeof this.chart.getDom === 'function' && this.chart.getDom() !== el) {
          this.chart.dispose();
          this.chart = null;
        } else {
          return;
        }
      }
      this.chart = echarts.init(el);

      // 使用 zr 全局监听点击，提高点击灵敏度（特别是针对 path 图形和空白区域）
      this.chart.getZr().on('click', (params) => {
        const pointInPixel = [params.offsetX, params.offsetY];
        if (this.chart.containPixel('grid', pointInPixel)) {
          const xIndex = this.chart.convertFromPixel({ seriesIndex: 0 }, pointInPixel)[0];
          if (xIndex >= 0 && xIndex < this.list.length) {
            this.selectedIndex = Math.round(xIndex);
            this.isExpand = false;
            this.renderChart();
          }
        }
      });

      this._onResize = () => {
        this.updateChartSize();
        if (this.chart) this.chart.resize();
      };
      window.addEventListener('resize', this._onResize, { passive: true });
    },
    updateChartSize() {
      const base = this.$refs.chartScroll ? this.$refs.chartScroll.clientWidth : 320;
      this.containerWidth = base;
      const count = this.list.length;
      const slotWidth = 72; // 每个山峰的占据宽度
      const totalWidth = count * slotWidth + 20;
      this.chartWidth = Math.max(base, totalWidth);
    },
    formatNumber(n) {
      if (!n && n !== 0) return "0";
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    formatRationStr(ration) {
      //ration 返回7.84%，或者 占比--% 没有占比添加占比两个字
      if (!ration) return "占比--%";
      if (String(ration).includes("占比")) return String(ration);
      return "占比" + String(ration);
    },

    renderChart() {
      if (!this.list.length) return;
      this.updateChartSize();
      this.$nextTick(() => {
        this.ensureChart();
        if (!this.chart) return;

        const names = this.list.map(it => it.label);
        const values = this.list.map(it => it.total);
        const maxVal = Math.max(...values, 1);

        // 更加尖锐且优雅的山峰路径
        const mountainPath = 'M0 100 C 15 100, 48 50, 50 0 C 52 50, 85 100, 100 100 Z';

        const option = {
          grid: { top: 40, bottom: 40, left: 10, right: 10 },
          xAxis: {
            data: names,
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: {
              interval: 0,
              color: '#1E2226',
              fontSize: 12,
              lineHeight: 17,
              formatter: (val) => {
                if (!val) return '';
                return val.length > 4 ? val.substring(0, 4) + '...' : val;
              }
            }
            // axisLabel: {
            //   color: (val, index) => index === this.selectedIndex ? '#36B374' : '#666',
            //   fontSize: 14,
            //   fontWeight: (val, index) => index === this.selectedIndex ? 600 : 400,
            //   interval: 0,
            //   padding: [8, 0, 0, 0]
            // }
          },
          yAxis: { show: false },
          series: [
            {
              type: 'bar',
              barWidth: '100%',
              silent: true,
              z: 2, // 确保背景在地板层
              data: values.map((v, i) => ({
                value: maxVal * 1.5,
                itemStyle: {
                  color: i === this.selectedIndex ? '#F7F8FA' : 'transparent'
                }
              }))
            },
            {
              type: 'pictorialBar',
              symbol: 'path://' + mountainPath,
              barWidth: '100%', // 与背景对齐
              barGap: '-100%', // 关键：使此系列在同一个类目下重合展示
              z: 10, // 确保前景在高位
              data: values.map((v, i) => {
                // 保底逻辑：当某项有值但非常小时（小于最大值的 2%），给定一个最小显示高度，防止柱子不可见无法点击
                let displayValue = v;
                if (v > 0 && v < maxVal * 0.02) {
                  displayValue = maxVal * 0.02;
                }
                return {
                  value: displayValue,
                  itemStyle: {
                    color: i === this.selectedIndex
                      ? '#36B374'
                      : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: 'rgb(74, 184, 126)' },
                        { offset: 1, color: 'rgb(245, 250, 245)' }
                      ])
                  }
                };
              }),
              label: { show: false }
            }
          ]
        };

        this.chart.setOption(option, true);
        this.chart.resize();
      });
    },
    onLoad() {
      const { areaId, beginTime, endTime, rule, channel } = this.filterParams;
      const params = {
        cityId: areaId,
        startDate: beginTime,
        endDate: endTime,
        rule: rule,
        channel: channel
      };

      this.loading = true;
      APIS.getClassifyCount(params).then(res => {
        if (res && res.ret === 0) {
          this.list = Array.isArray(res.data) ? res.data : [];
        }
      }).finally(() => {
        this.loading = false;
        this.$nextTick(() => this.renderChart());
      });
    }
  }
};
</script>

<style lang="less" scoped>
.card {
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
}

.tips-right {
  position: absolute;
  right: 0;
  top: 0;
  font-size: 12px;
  color: #999;
  z-index: 10;
}

.chart-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    display: none;
  }
}

.chart {
  height: 180px;
}

.scroll-indicator-bg {
  width: 40px;
  height: 3px;
  background: #F0F0F0;
  margin: 0 auto;
  position: relative;
  border-radius: 2px;
  overflow: hidden;
}

.scroll-indicator-bar {
  position: absolute;
  height: 100%;
  background: #36B374;
  border-radius: 2px;
  transition: left 0.1s linear;
}

.detail-card {
  border: 1px solid #EBEDF0;
  border-radius: 8px;
  overflow: hidden;

  .detail-header {
    background: #F8F9FA;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .detail-name {
      font-size: 16px;
      font-weight: 700;
      color: #1E2226;
    }

    .detail-summary {
      display: flex;
      align-items: baseline;

      .total-num {
        font-size: 18px;
        font-weight: 700;
        color: #1E2226;
      }

      .unit {
        font-size: 12px;
        color: #999;
        margin-left: 2px;
      }

      .ratio-label {
        font-size: 12px;
        color: #999;
      }

      .ratio-val {
        font-size: 16px;
        font-weight: 700;
        color: #1E2226;
        margin-left: 4px;
      }
    }
  }

  .detail-body {
    .sub-title {
      font-size: 14px;
      color: #999;
    }

    .issue-item {
      border-bottom: 1px solid #F0F0F0;

      &:last-child {
        border-bottom: none;
      }

      .issue-name {
        font-size: 14px;
        color: #666;
        flex: 1;
        margin-right: 12px;
      }

      .issue-stats {
        .val {
          font-size: 14px;
          color: #666;
        }

        .ratio {
          font-size: 14px;
          color: #999;
        }
      }
    }

    .expand-btn {
      color: #36B374;
      font-size: 14px;
    }
  }
}

.state-text {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}
</style>
